import { Injectable } from '@angular/core';
import {
  DataConversionCalculationDto,
  DataConversionDto,
  DataConversionUserSetting,
  UnitConversionSecureAction,
  UserSettings,
  ValueType,
} from '@deprecated/api-interfaces';
import { DataConversionBaseService } from '@ird/shared-utils';
import { Observable, firstValueFrom, map } from 'rxjs';
import { IStructuredUnit, UnitsConversionEnum } from '../models';
import { DataConversionRepositoryHttp } from '../repositories';
import { ActionsService } from './actions.service';
import { PrecisionService } from './precision.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class DataConversionService extends DataConversionBaseService {
  private callStack: Map<ValueType, Promise<any>> = new Map();

  constructor(
    private dataConversionRepository: DataConversionRepositoryHttp,
    private userService: UserService,
    private precisionService: PrecisionService,
    private actionService: ActionsService,
  ) {
    super();
  }

  findAll(): Observable<DataConversionDto[]> {
    return this.dataConversionRepository.findAll();
  }

  findAllUnits(): Observable<DataConversionDto[]> {
    return this.dataConversionRepository.findAllUnits();
  }

  async convertValue(value: number, dataConversionDto: DataConversionDto): Promise<number | undefined> {
    if (typeof value !== 'number') {
      return undefined;
    }
    const calculationDto = await this.validateAndGetCalculationDto(value, dataConversionDto);
    const convertedValue = await this.convert<number>(value, false, calculationDto);

    return this.setPrecision(convertedValue, dataConversionDto);
  }

  async convertLabel(label: string, dataConversionDto: DataConversionDto): Promise<string> {
    if (typeof label !== 'string') {
      return undefined;
    }
    const calculationDto = await this.validateAndGetCalculationDto(label, dataConversionDto);
    return this.convert<string>(label, true, calculationDto);
  }

  async convertValueBack(value: number, dataConversionDto: DataConversionDto): Promise<number> {
    const calculationDto = await this.validateAndGetCalculationDto(value, dataConversionDto, true);
    return this.convert<number>(value, false, calculationDto);
  }

  async convertArrayOfValues(dataConversionDto: DataConversionDto, data: Array<Array<number>>): Promise<Array<Array<number>>> {
    const convertedArrayOfValues: number[][] = [];

    for (const array of data) {
      const convertedArray: number[] = [];

      convertedArray.push(array[0]);
      for (let i = 1; i < array.length; i++) {
        const convertedValue = await this.convertValue(array[i], dataConversionDto);
        convertedArray.push(convertedValue);
      }
      convertedArrayOfValues.push(convertedArray);
    }

    return convertedArrayOfValues;
  }

  async validateAndGetCalculationDto(
    value: number | string,
    dataConversionDto: DataConversionDto,
    convertBack?: boolean,
  ): Promise<DataConversionCalculationDto> {
    // validate input or return
    if (!this.inputValid(value, dataConversionDto)) {
      return undefined;
    }

    // check if userSetting has entry for given valueType, return if not
    const dataConversionUserSetting = await this.getAndValidateUserSettings(dataConversionDto);
    if (!dataConversionUserSetting) {
      return undefined;
    }

    return this.loadDataConversionCalculationDto(
      dataConversionDto.valueType,
      convertBack ? dataConversionUserSetting.targetUnit : dataConversionDto.sourceUnit,
      convertBack ? dataConversionDto.sourceUnit : dataConversionUserSetting.targetUnit,
    );
  }

  async getAndValidateUserSettings(dataConversionDto: DataConversionDto): Promise<DataConversionUserSetting | void> {
    const userSettings = await this.userService.getUserSettings();

    if (DataConversionService.userSettingsValid(userSettings, dataConversionDto)) {
      return userSettings.dataConversions.find(
        (conversion: DataConversionUserSetting) => conversion.valueType === dataConversionDto.valueType,
      );
    }
  }

  async loadDataConversionCalculationDto(
    valueType: ValueType,
    sourceUnit: string,
    targetUnit: string,
  ): Promise<DataConversionCalculationDto> {
    // if source unit === targetUnit return empty object
    // if degree symbol is missing return empty object
    if (sourceUnit === targetUnit || `°${sourceUnit}` === targetUnit) {
      return {} as DataConversionCalculationDto;
    }

    // check if item is in cache
    const cachedData = this.getCacheItem(this.formatCacheKey(valueType, sourceUnit, targetUnit));
    if (cachedData) {
      return cachedData;
    } else {
      // Check if the call for specific valueType is already going on
      // if so return this call
      if (this.callStack.get(valueType)) {
        return this.callStack.get(valueType);
      } else {
        // Put call into callStack
        this.callStack.set(
          valueType,
          firstValueFrom(
            this.dataConversionRepository.getCalculationDto({
              sourceUnit: sourceUnit,
              targetUnit: targetUnit,
              valueType: valueType,
            }),
          ),
        );
        // Await call and add item to cache
        const dataConversionCalculationDto = await this.callStack.get(valueType);

        // Set cache
        this.setCacheItem(dataConversionCalculationDto);

        // delete call from callStack
        this.callStack.delete(valueType);
        return dataConversionCalculationDto;
      }
    }
  }

  private async setPrecision(value: number, dataConversionDto: DataConversionDto): Promise<number> {
    const precision = await this.precisionService.convert({
      value,
      valueType: dataConversionDto?.valueType,
      sourceUnit: dataConversionDto?.sourceUnit,
      sensorPrecision: dataConversionDto?.sensorPrecision,
    });

    return precision ?? value;
  }

  private inputValid(value: number | string, dataConversionDto: DataConversionDto): boolean {
    return !(this.isNullOrUndefined(value) || !dataConversionDto?.valueType || !dataConversionDto?.sourceUnit);
  }

  private static userSettingsValid(userSettings: UserSettings, dataConversionDto: DataConversionDto): boolean {
    return !(
      !userSettings ||
      !userSettings?.dataConversions ||
      !userSettings.dataConversions.find((conversion: DataConversionUserSetting) => conversion.valueType === dataConversionDto.valueType) ||
      !userSettings.dataConversions.find((conversion: DataConversionUserSetting) => conversion.valueType === dataConversionDto.valueType)
        .targetUnit
    );
  }

  getStructuredUnits(): Observable<IStructuredUnit[]> {
    return this.findAllUnits().pipe(
      map((unstructuredUnits: DataConversionDto[]) => {
        if (!unstructuredUnits?.length) {
          return [];
        }

        unstructuredUnits.sort((a, b) => a?.targetUnit?.localeCompare(b?.targetUnit));

        const structuredUnits: IStructuredUnit[] = [];

        unstructuredUnits.forEach((unit: DataConversionDto) => {
          const secureAction = UnitConversionSecureAction['VIEW_' + unit.valueType.toUpperCase() + '_' + unit.targetUnit];
          if (this.actionService.hasAction(secureAction)) {
            const index = structuredUnits.findIndex((u) => u?.type?.toLowerCase() === unit?.valueType?.toLowerCase());

            if (index < 0) {
              structuredUnits.push({
                name: this.getUnitNameFromConversionEnum(unit.valueType),
                translationKey: DataConversionService.getUnitTranslationKey(unit.valueType),
                type: unit.valueType,
                unitsList: [unit],
              });
            } else {
              const duplicate = structuredUnits[index].unitsList.find((u) => u.targetUnit === unit.targetUnit);
              if (!duplicate) {
                structuredUnits[index].unitsList.push(unit);
              }
            }
          }
        });

        structuredUnits.sort((a, b) => a.name.localeCompare(b.name));
        return structuredUnits;
      }),
    );
  }

  private getUnitNameFromConversionEnum(enumKey: string): string {
    return UnitsConversionEnum[enumKey] ?? '';
  }

  // Only known value types get a key — an unknown one would otherwise render the raw key string.
  private static getUnitTranslationKey(enumKey: string): string | undefined {
    return UnitsConversionEnum[enumKey] ? `DEVICE_DETAIL_UNITS_CONVERSION.VALUE_TYPES.${enumKey}` : undefined;
  }
}
